import React, { useEffect, useState } from "react";
import Amplify, {
  API,
  Auth,
  graphqlOperation,
  Hub,
  Logger,
  I18n,
} from "aws-amplify";
import moment from "moment";
import "moment/locale/de";
import awsconfig from "./aws-exports";
import { v4 as uuidv4 } from "uuid";
import { withCookies } from "react-cookie";
import * as mutations from "./graphql/mutations";
import { getOrganization as getMainOrganization } from "./graphql/queries";
import StateProvider from "./state";
import translations from "./locale/translations";
import "./App.scss";
import german from "antd/es/date-picker/locale/de_DE";
import english from "antd/es/date-picker/locale/en_GB";

import Theme from "./frontend/theme/Theme";
import CookiePopup from "./frontend/CookiePopup";
import Loader from "./Loader";
import ability, { defineRulesFor } from "./config/ability";
import CustomAuth from "./frontend/auth/CustomAuth";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { Status } from "./API";

Amplify.configure(awsconfig);
Auth.configure({
  authenticationFlowType: "USER_PASSWORD_AUTH",
});

const logger = new Logger("My-Logger");

const getUserId = `
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      firstName
      lastName
      email
      teamsTokens {
        accessToken
        refreshToken
        expires_in
      }
      sessions {
        items {
          session {
            id
          }
        }
      }
      events {
        items {
          event {
            id
          }
        }
      }
    }
  }
`;

function App(props) {
  const [loading, setLoading] = useState(true);
  const [userGroups, setUserGroups] = useState([]);
  const [userId, setUserId] = useState(null);
  const [userFirstName, setUserFirstName] = useState("");
  const [anonymousUserName, setAnonymousUserName] = useState(
    localStorage.getItem("anonymousUserName")
  );
  const [language, setLanguage] = useState(
    localStorage.getItem("language") ||
      process.env.REACT_APP_DEFAULT_LANUGAGE ||
      "de"
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [consentCookie, setConsentCookie] = useState(false);
  const [antdLocale, setAntdLocale] = useState(
    localStorage.getItem("language") === "en" ? english : german
  );
  const [globalUser, setGlobalUser] = useState();
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [globalOrganization, setGlobalOrganization] = useState({});

  const upcomingSesssionsRef = React.createRef();
  const archivedSessionsRef = React.createRef();
  const currentSessionsRef = React.createRef();

  useEffect(() => {
    if (globalOrganization?.cssOverride) {
      var style = document.createElement("style");
      style.innerHTML = globalOrganization.cssOverride;
      document.getElementsByTagName("head")[0].appendChild(style);
    }
  }, [globalOrganization]);

  const updateAnonymousUserName = (name) => {
    setAnonymousUserName(name);
    localStorage.setItem("anonymousUserName", name);
  };

  const scrollToRef = () => {
    upcomingSesssionsRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const listener = async (data) => {
    switch (data.payload.event) {
      case "signOut":
        logger.error("user signed out");
        window.location.href = "/";
        return;
      case "signIn":
        await fetchCredentials();
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    fetchCredentials();
    Hub.listen("auth", listener);
    I18n.putVocabularies(translations);
    I18n.setLanguage(language);
    moment.locale(language);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkCookie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkCookie = () => {
    const { cookies } = props;
    const consentCookie = cookies.get("consentCookie");
    if (consentCookie === "consentGiven") {
      setConsentCookie(true);
    }
    if (consentCookie === "noConsent") {
      setConsentCookie(true);
    }
  };

  const fetchCredentials = async () => {
    setLoading(true);
    let {
      data: { getOrganization },
    } = await API.graphql(
      graphqlOperation(getMainOrganization, {
        id: process.env.REACT_APP_ORG_ID,
      })
    );
    setGlobalOrganization(getOrganization);

    // Check if the user is authenticated
    let currentUserCredentials = await Auth.currentUserCredentials();
    let { authenticated } = currentUserCredentials;

    if (authenticated) {
      let user = await Auth.currentSession({ bypassCache: true });
      const {
        idToken: {
          payload: { sub },
        },
      } = user;

      setUserFirstName(user.idToken.payload.given_name);
      setIsAuthenticated(true);
      try {
        const {
          data: { getUser },
        } = await API.graphql({
          query: getUserId,
          variables: { id: sub },
          authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        }).catch((e) => console.error(e));

        const { given_name, family_name, email } = user.idToken.payload;

        //Check if user in DynamoDB already exists
        if (getUser === null) {
          await API.graphql({
            query: mutations.createUser,
            variables: {
              input: {
                id: sub,
                firstName: given_name,
                lastName: family_name,
                email,
                lastLoginTime: new Date(),
                status: Status.ACTIVE,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          });
        } else {
          await API.graphql({
            query: mutations.updateUser,
            variables: {
              input: {
                id: sub,
                status: Status.ACTIVE,
                lastLoginTime: new Date(),
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          });
        }

        setGlobalUser(getUser);
        setUserEnrollments(
          getUser?.sessions?.items?.map((enr) => enr?.session?.id)
        );
        setUserEvents(getUser?.events?.items?.map((e) => e?.event?.id));
      } catch (e) {
        console.log(e);
      }
      let userGroup = user.accessToken.payload["cognito:groups"];
      setUserGroups(userGroup === undefined ? [] : userGroup);
      ability.update(defineRulesFor(userGroup));
      setUserId(sub);
      setLoading(false);
      // If no authenticated user
    } else {
      setUserId(currentUserCredentials.data.IdentityId || uuidv4());
      let input = {
        id: currentUserCredentials.data.IdentityId,
        firstName: "Anonymous",
        lastName: "User",
        isTemporary: true,
      };
      setUserGroups([]);
      setLoading(false);
    }
  };

  const popup = () => {
    if (!consentCookie) {
      return (
        <CookiePopup
          cookies={props.cookies}
          setConsentCookie={setConsentCookie}
        />
      );
    }
    return null;
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
    I18n.setLanguage(lang);
    moment.locale(lang);
    if (lang === "de") {
      setAntdLocale(german);
    } else {
      setAntdLocale(english);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Theme>
            <StateProvider
              state={{
                userGroups,
                userId,
                setUserId,
                userFirstName,
                anonymousUserName,
                updateAnonymousUserName,
                setLanguage: changeLanguage,
                language,
                upcomingSesssionsRef,
                currentSessionsRef,
                archivedSessionsRef,
                scrollToRef,
                isAuthenticated,
                antdLocale,
                globalUser,
                userEnrollments,
                setUserEnrollments,
                userEvents,
                setUserEvents,
                globalOrganization,
                loginEnabled: process.env.REACT_APP_SIGNIN !== "HIDE",
              }}
            >
              {globalOrganization?.loginRequired ? (
                <CustomAuth>
                  {popup()}
                  {props.children}
                </CustomAuth>
              ) : (
                <>
                  {popup()}
                  {props.children}
                </>
              )}
            </StateProvider>
          </Theme>
        </>
      )}
    </>
  );
}

export default withCookies(App);
